import { useSyncExternalStore } from 'react';

/**
 * Warm boot: this document was opened by the host to have game-ui already running, and must stay inert
 * until a launch message arrives.
 *
 * Why inert has to be explicit rather than a route choice: `useAuth` seeds `isAuthenticated` from the
 * `version` URL param alone (`useAuth.ts`, `useState(isNewGameUiAuth)`), so a version=3 document is
 * "authenticated" from its first render at any route — which connects both wallet sockets and fetches
 * the profile. A warm frame that did that would give every lobby visitor a duplicate socket pair and a
 * duplicate login.
 *
 * Why a module store rather than a React provider: the profile fetch that needs gating lives inside
 * `AuthProvider`, which is the outermost provider in App.tsx. A provider would have to wrap everything
 * and still could not be read above itself. This has no ordering constraints.
 *
 * Absent param means not warm. That is what keeps this invisible to every existing client — notably the
 * mobile app, which builds its own game-ui URL and never sends `warm`.
 */
export function readWarmParam(search: string): boolean {
  return new URLSearchParams(search).get('warm') === '1';
}

let warm = readWarmParam(globalThis.location?.search ?? '');

const listeners = new Set<() => void>();

export function isWarmBoot(): boolean {
  return warm;
}

/**
 * Leave warm mode: a launch arrived, so sockets may connect and the profile may load. One-way — a
 * document never returns to warm. The host recycles the iframe instead, which is what keeps a second
 * launch from ever reusing the first game's credentials.
 */
export function exitWarmBoot(): void {
  if (!warm) {
    return;
  }
  warm = false;
  for (const listener of listeners) {
    listener();
  }
}

/** Exported so the notification path is testable; useIsWarmBoot is the normal consumer. */
export function subscribeToWarmBoot(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function useIsWarmBoot(): boolean {
  return useSyncExternalStore(subscribeToWarmBoot, isWarmBoot, () => false);
}

/** Test seam — module state would otherwise leak between cases. */
export function __resetWarmBootForTest(value: boolean): void {
  warm = value;
  listeners.clear();
}
